import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';

// ============ EMPLOYEES COLLECTION ============

// Get all employees
export const fetchEmployees = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'employees'));
    const employees = [];
    querySnapshot.forEach((doc) => {
      employees.push({
        ...doc.data(),
        id: doc.id
      });
    });
    return employees;
  } catch (error) {
    console.error('Error fetching employees:', error);
    return [];
  }
};

// Real-time listener for employees
export const onEmployeesChange = (callback) => {
  const q = query(collection(db, 'employees'), orderBy('name'));
  return onSnapshot(q, (snapshot) => {
    const employees = [];
    snapshot.forEach((doc) => {
      employees.push({
        ...doc.data(),
        id: doc.id
      });
    });
    callback(employees);
  }, (error) => {
    console.error('Error listening to employees:', error);
  });
};

// Add a new employee
export const addEmployee = async (employeeData) => {
  try {
    const docRef = await addDoc(collection(db, 'employees'), {
      ...employeeData,
      createdAt: new Date().toISOString()
    });
    return { ...employeeData, id: docRef.id };
  } catch (error) {
    console.error('Error adding employee:', error);
    throw error;
  }
};

// Update an employee
export const updateEmployee = async (employeeId, updatedData) => {
  try {
    const employeeRef = doc(db, 'employees', employeeId);
    await updateDoc(employeeRef, {
      ...updatedData,
      updatedAt: new Date().toISOString()
    });
    return { id: employeeId, ...updatedData };
  } catch (error) {
    console.error('Error updating employee:', error);
    throw error;
  }
};

// Delete an employee
export const deleteEmployee = async (employeeId) => {
  try {
    await deleteDoc(doc(db, 'employees', employeeId));
    return true;
  } catch (error) {
    console.error('Error deleting employee:', error);
    throw error;
  }
};

// ============ LEAVES COLLECTION ============

// Get all leaves
export const fetchLeaves = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'leaves'));
    const leaves = [];
    querySnapshot.forEach((doc) => {
      leaves.push({
        ...doc.data(),
        id: doc.id
      });
    });
    return leaves;
  } catch (error) {
    console.error('Error fetching leaves:', error);
    return [];
  }
};

// Seed initial leaves if collection is empty
export const seedLeaves = async (leaveList) => {
  try {
    if (!leaveList || leaveList.length === 0) return [];
    const addedLeaves = [];
    for (const leaveData of leaveList) {
      const { id: _legacyId, ...fields } = leaveData;
      const docRef = await addDoc(collection(db, 'leaves'), {
        ...fields,
        createdAt: new Date().toISOString()
      });
      addedLeaves.push({ ...fields, id: docRef.id });
    }
    return addedLeaves;
  } catch (error) {
    console.error('Error seeding leaves:', error);
    throw error;
  }
};

// Real-time listener for leaves
export const onLeavesChange = (callback) => {
  const q = query(collection(db, 'leaves'), orderBy('from'));
  return onSnapshot(q, (snapshot) => {
    const leaves = [];
    snapshot.forEach((doc) => {
      leaves.push({
        ...doc.data(),
        id: doc.id
      });
    });
    callback(leaves);
  }, (error) => {
    console.error('Error listening to leaves:', error);
  });
};

// Add a new leave request
export const addLeave = async (leaveData) => {
  try {
    const docRef = await addDoc(collection(db, 'leaves'), {
      ...leaveData,
      createdAt: new Date().toISOString()
    });
    return { ...leaveData, id: docRef.id };
  } catch (error) {
    console.error('Error adding leave:', error);
    throw error;
  }
};

// Seed initial employees if collection is empty
export const seedEmployees = async (employeeList) => {
  try {
    if (!employeeList || employeeList.length === 0) return [];
    const addedEmployees = [];
    for (const employeeData of employeeList) {
      const { id: _legacyId, ...fields } = employeeData;
      const docRef = await addDoc(collection(db, 'employees'), {
        ...fields,
        createdAt: new Date().toISOString()
      });
      addedEmployees.push({ ...fields, id: docRef.id });
    }
    return addedEmployees;
  } catch (error) {
    console.error('Error seeding employees:', error);
    throw error;
  }
};

// Update a leave request
export const updateLeave = async (leaveId, updatedData) => {
  try {
    const leaveRef = doc(db, 'leaves', leaveId);
    await updateDoc(leaveRef, {
      ...updatedData,
      updatedAt: new Date().toISOString()
    });
    return { id: leaveId, ...updatedData };
  } catch (error) {
    console.error('Error updating leave:', error);
    throw error;
  }
};

// Delete a leave request
export const deleteLeave = async (leaveId) => {
  try {
    await deleteDoc(doc(db, 'leaves', leaveId));
    return true;
  } catch (error) {
    console.error('Error deleting leave:', error);
    throw error;
  }
};

// Get leaves by employee ID
export const fetchLeavesByEmployee = async (employeeId) => {
  try {
    const q = query(collection(db, 'leaves'), where('eId', '==', employeeId));
    const querySnapshot = await getDocs(q);
    const leaves = [];
    querySnapshot.forEach((doc) => {
      leaves.push({
        ...doc.data(),
        id: doc.id
      });
    });
    return leaves;
  } catch (error) {
    console.error('Error fetching leaves for employee:', error);
    return [];
  }
};

// Get leaves by status
export const fetchLeavesByStatus = async (status) => {
  try {
    const q = query(collection(db, 'leaves'), where('status', '==', status));
    const querySnapshot = await getDocs(q);
    const leaves = [];
    querySnapshot.forEach((doc) => {
      leaves.push({
        ...doc.data(),
        id: doc.id
      });
    });
    return leaves;
  } catch (error) {
    console.error('Error fetching leaves by status:', error);
    return [];
  }
};
